import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { urlParamReplicaId, urlProjectSignatureStatus } from '../../Constants';
import { IAction, IAppReduxStore, SlicePropsWithData } from '../store';
import { apiFetch } from '../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectSignatureStatusState = {
    data: [],
    loading: false,
    error: null,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that fetches the available signature statuses for a given project database.
     * @param replicaId - ReplicaId for the target project database.
     * @returns Promise for the HTTP request.
     */
    getProjectSignatureStatus: async (replicaId: string): Promise<IProjectSignatureStatus[]> => {
        try {
            const url = urlProjectSignatureStatus.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectSignatureStatusState>) => {
        /**
         * Reducers for the 'getProjectDocStatus' thunk.
         */

        builder.addCase(
            getProjectSignatureStatus.pending,
            (state: Draft<IProjectSignatureStatusState>) => {
                state.data = null;
                state.loading = true;
                state.error = null;
            },
        );
        builder.addCase(
            getProjectSignatureStatus.fulfilled,
            (
                state: Draft<IProjectSignatureStatusState>,
                action: IAction<IProjectSignatureStatus[]>,
            ) => {
                state.loading = false;
                state.error = null;
                state.data = action.payload;
            },
        );
        builder.addCase(
            getProjectSignatureStatus.rejected,
            (state: Draft<IProjectSignatureStatusState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
                state.data = null;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const getProjectSignatureStatus = createAsyncThunk(
    'project/signatureStatus',
    thunks.getProjectSignatureStatus,
);

/**
 * Interface that describes the state object for this redux slice.
 */
export type IProjectSignatureStatusState = SlicePropsWithData<IProjectSignatureStatus[]>;

/**
 * Interface that describes a SignatureStatus configuration.
 */
export interface IProjectSignatureStatus {
    /** SignatureStatus status name. */
    status: string;

    /** SignatureStatus SVG Icon. */
    icon: string;

    /** SignatureStatus icon color. */
    color: string;

    /** SignatureStatus Display text translations */
    translations: { [id: string]: string };
}

/**
 * Create the redux slice.
 */
export const projectSignatureStatusSlice = createSlice({
    name: 'projectSignatureStatus',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

export const getSignatureStatusByStatusNumber =
    (state: IAppReduxStore) => (statusNumber: string) => {
        return (
            state.projectSignatureStatus?.data?.find(item => item.status === statusNumber) ?? null
        );
    };

/**
 * Export slice reducer as default.
 */
export default projectSignatureStatusSlice.reducer;
